import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    // Cache cart discount data from initial Liquid render.
    // Section rendering may lose collection metafield data, so we preserve it here.
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (priceContainer?.dataset?.discountPct) {
      this._cartDiscountPct = parseInt(priceContainer.dataset.discountPct, 10);
    }
    const labelEl = this.querySelector('.cart-discount-label');
    if (labelEl) {
      this._cartDiscountLabel = labelEl.textContent.trim();
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Formats a discounted price to match the original price format.
   * @param {string} originalPriceText - The original formatted price (e.g. "$3,504" or "3.504 €")
   * @param {number} discountPct - The discount percentage
   * @returns {{ original: string, discounted: string } | null}
   */
  _calcDiscountedPrice(originalPriceText, discountPct) {
    const text = originalPriceText.trim();
    if (!text) return null;

    // Find where digits start and end
    const firstDigit = text.search(/\d/);
    const lastDigit = text.length - 1 - [...text].reverse().join('').search(/\d/);
    if (firstDigit === -1) return null;

    const prefix = text.slice(0, firstDigit);
    const suffix = text.slice(lastDigit + 1);
    const numberPart = text.slice(firstDigit, lastDigit + 1);

    // Parse: remove all non-digits to get the integer value
    const num = parseInt(numberPart.replace(/\D/g, ''), 10);
    if (isNaN(num)) return null;

    const discounted = Math.round(num * (100 - discountPct) / 100);

    // Re-format with same thousands separator
    let formatted;
    if (numberPart.includes(',')) {
      formatted = discounted.toLocaleString('en-US');
    } else if (numberPart.includes('.')) {
      formatted = discounted.toLocaleString('de-DE');
    } else {
      formatted = discounted.toString();
    }

    return {
      original: text,
      discounted: prefix + formatted + suffix
    };
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) {
      const newHasDiscount = newPrice.querySelector('.cart-discount-pricing');

      if (!newHasDiscount && this._cartDiscountPct) {
        // Section rendering lost the cart discount — reconstruct it from cached data
        const newPriceEl = newPrice.querySelector('.price');
        if (newPriceEl) {
          const result = this._calcDiscountedPrice(newPriceEl.textContent, this._cartDiscountPct);
          if (result) {
            newPrice.innerHTML = `
              <div class="cart-discount-pricing">
                <span class="compare-at-price">${result.original}</span>
                <span class="price cart-discount-price">${result.discounted}</span>
              </div>
              <div class="cart-discount-badge">
                <span class="cart-discount-label">${this._cartDiscountLabel || ''}</span>
              </div>`;
            newPrice.dataset.discountPct = String(this._cartDiscountPct);
          }
        }
      }

      currentPrice.replaceWith(newPrice);
    }

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
